/**
 * Mockup 05 · Concluído — A completo (+4) · B sem formulário (+1).
 * Celebração só em CSS (`animate-ping` curto) e `motion-reduce:animate-none`.
 * A pílula "Ranking da loja: #3 ↑1" do mockup depende de `/api/gamification/me`
 * (Parte 4) — a Parte 5 insere aqui o componente de posição.
 */
import { DEFAULT_GAMIFICATION_RULES, type GamificationPeriod, type LeadCaptureSession } from '@troia-v3/types';
import React from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { Button, ModalContent, ModalFooter } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { periodInLabel } from '@/shared/utils/gamificationPeriod';

import { leadHref } from './CaptureFilledStep';
import { CaptureInfoBox, CaptureKv } from './captureUi';

const capturePoints = DEFAULT_GAMIFICATION_RULES.find((r) => r.key === 'lead_captured')?.points ?? 1;
const formPoints = DEFAULT_GAMIFICATION_RULES.find((r) => r.key === 'form_completed')?.points ?? 3;

export interface CaptureDoneStepProps {
  session: LeadCaptureSession;
  /**
   * Pontos REAIS da sessão (`LeadCaptureSession.pointsEarned`, gravado pelo
   * backend a partir do livro-caixa quando a sessão virou `done`). Zero =
   * gamificação/regra desligada, cap diário batido ou nada confirmado.
   */
  points: number;
  /** Período da gamificação da empresa — "+4 pontos {no mês|na semana|no trimestre}". */
  gamificationPeriod?: GamificationPeriod;
  onClose: () => void;
  onNewSession: () => void;
}

export const CaptureDoneStep: React.FC<CaptureDoneStepProps> = ({ session, points, gamificationPeriod = 'month', onClose, onNewSession }) => {
  const { state } = useAuth();
  const confirmed = Boolean(session.confirmedAt) && !session.wasExistingLead;
  const formDone = Boolean(session.formCompletedAt);
  const hasForm = Boolean(session.capture.formId);
  const hasChannel = Boolean(session.capture.channelId);
  const variant = formDone ? 'full' : 'no-form';
  /**
   * G4: com `points === 0` a captura não pontuou de verdade (gamificação
   * desligada, regra off, cap diário). As linhas por regra deixam de mostrar
   * o "+N" do catálogo padrão — prometer "+1 · +3" embaixo de um total zero
   * era a contradição mais visível da tela.
   */
  const scored = points > 0;
  /** "A." / "A e B." / "A, B e C." — mockup 05-A junta o resumo com "e" antes do último item, nunca vírgula. */
  const joinWithAnd = (items: string[]): string =>
    items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;
  const summary = joinWithAnd([
    'Lead criado',
    ...(confirmed ? ['confirmado no WhatsApp'] : []),
    ...(formDone ? ['formulário preenchido'] : []),
  ]);

  return (
    <>
      <ModalContent>
        <div data-testid="capture-done">
          <div className="text-center pt-1.5" data-testid="capture-done-variant" data-variant={variant}>
            <div className="relative w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: '#f3e8ff', color: '#7c3aed' }}>
              <Icon name="star" size={30} />
              {variant === 'full' && <span className="absolute inset-0 rounded-full animate-ping motion-reduce:animate-none" style={{ background: '#ede9fe', animationIterationCount: 1, animationDuration: '1.1s' }} />}
            </div>
            <h4 className="m-0 text-lg font-bold tracking-[-0.02em]" style={{ color: '#1e293b' }}>{variant === 'full' ? `Boa, ${state.user?.firstName ?? ''}!` : 'Lead registrado'}</h4>
            <p className="mt-1 mb-4 text-[13px]" style={{ color: '#64748b' }}>
              {variant === 'full'
                ? `${summary}.`
                : hasForm
                  ? `${confirmed ? 'Confirmado no WhatsApp. ' : ''}Você pulou o formulário — dá pra preencher depois.`
                  : `${summary}.`}
            </p>
          </div>
          <CaptureKv rows={[
            { k: 'Lead', v: (
              <span className="inline-flex items-center gap-1.5">
                {session.customer?.name ?? '—'}
                <a href={leadHref(session)} target="_blank" rel="noreferrer" className="inline-flex" style={{ color: '#7c3aed' }}><Icon name="external-link" size={14} /></a>
              </span>
            ) },
            ...(variant === 'full' ? [{ k: 'Etapa', v: `${session.stepName} · ${session.funnelName}` }] : []),
            { k: 'Lead captado e confirmado', v: <span style={{ color: confirmed && scored ? '#7c3aed' : '#94a3b8', fontWeight: confirmed ? 600 : 500 }}>{confirmed ? (scored ? `+${capturePoints}` : 'confirmado') : hasChannel ? 'não confirmado' : '—'}</span>, mono: true },
            ...(hasForm ? [{ k: 'Formulário preenchido', v: <span style={{ color: formDone && scored ? '#7c3aed' : '#94a3b8', fontWeight: formDone ? 600 : 500 }}>{formDone ? (scored ? `+${formPoints}` : 'preenchido') : 'pendente'}</span>, mono: true }] : []),
          ]} />
          <div className="flex items-baseline justify-center gap-1.5 mt-3.5 mb-1">
            <span className="text-[34px] font-bold tracking-[-0.03em]" style={{ color: scored ? '#7c3aed' : '#94a3b8', fontVariantNumeric: 'tabular-nums' }} data-testid="capture-done-points">{scored ? `+${points}` : '0'}</span>
            <span className="text-[13px] font-medium" style={{ color: '#64748b' }} data-testid="capture-done-points-caption">
              {scored ? `${points === 1 ? 'ponto' : 'pontos'} ${periodInLabel(gamificationPeriod)}` : 'sem pontos nesta captura'}
            </span>
          </div>
          {variant === 'full'
            ? scored && <div className="text-xs text-center mt-3" style={{ color: '#94a3b8' }}>Se a venda fechar, marque o lead como ganho com o valor — vale +10.</div>
            : hasForm && (
              <CaptureInfoBox tone="slate" icon="info" className="mt-2.5">
                O formulário fica disponível na aba <b>Checklists/Formulários</b> do lead.{scored ? <> Preenchendo, você ganha os +{formPoints}.</> : null}
              </CaptureInfoBox>
            )}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} data-testid="capture-done-close">Fechar</Button>
        <span className="flex-1" />
        <Button variant="ghost" icon="external-link" onClick={() => window.open(leadHref(session), '_blank')} data-testid="capture-done-open-lead">Abrir lead</Button>
        <Button variant="primary" icon="qr-code" onClick={onNewSession} data-testid="capture-done-new">Novo atendimento</Button>
      </ModalFooter>
    </>
  );
};
